import { AuthContext } from './contexts/AuthContext/definition'
import { ImpersonationContext } from './contexts/ImpersonationContext/definition'

import { Layout, Spin } from 'antd'
import LayoutContent from './components/LayoutContent'
import LayoutHeader from './components/LayoutHeader'
import LayoutSider from './components/LayoutSider'
import { ProtectedRoute } from './components/ProtectedRoute'
import { lazy, useContext, useEffect, useState, Suspense, useMemo } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ScriptPage } from './pages/ScriptPage'

import { saveLoginMissLocation } from './lib/session/saveLoginMissLocation'

const AgentTransferPage = lazy(() => import('./pages/AgentTransferPage'))
const IndexPage = lazy(() => import('./pages/IndexPage'))
const OneLoginPage = lazy(() => import('./pages/OneLoginPage'))
const LogoutPage = lazy(() => import('./pages/LogoutPage'))
import LoginPage from './pages/LoginPage'
const SchedulingPage = lazy(() => import('./pages/SchedulingPage'))
const KPIPage = lazy(() => import('./pages/KPIPage'))
const InboxPage = lazy(() => import('./pages/InboxPage'))
const SmsPage = lazy(() => import('./pages/SmsPage'))
const UsersPage = lazy(() => import('./pages/Admin/UsersPage'))
const CreateUserPage = lazy(
  () => import('./pages/Admin/UsersPage/CreateUserPage'),
)
const RolesPage = lazy(() => import('./pages/Admin/RolesPage'))
const CreateRolePage = lazy(
  () => import('./pages/Admin/RolesPage/CreateRolePage'),
)
const PermissionsPage = lazy(() => import('./pages/Admin/PermissionsPage'))
const CreatePermissionPage = lazy(
  () => import('./pages/Admin/PermissionsPage/CreatePermissionPage'),
)
const AttendancePage = lazy(() => import('./pages/AttendancePage'))

import styles from './App.module.css'
import MyFilesPage from './pages/MyFilesPage'
import { FileDisplayPage } from './pages/FileDisplayPage'
import { hasPermission } from './lib/auth/hasPermission'
import Impersonation from './components/Impersonation/Impersonation'
import CalendarIcon from './components/LayoutSider/calendarIcon'
import { SupplementalLinkType } from './types'

const NO_SIDER_ROUTES = new Set(['/scheduling', '/sms'])

const App: React.FC = () => {
  const location = useLocation()
  const { session, isLoaded, permissions } = useContext(AuthContext)
  const { impersonatedUserEmail, impersonateUser } =
    useContext(ImpersonationContext)

  const [collapsed, setCollapsed] = useState(true)
  const showHeader = true
  const showSider = useMemo(
    () => !NO_SIDER_ROUTES.has(location.pathname),
    [location.pathname],
  )

  const fullPage = useMemo(() => {
    const fullPageRoutes = [
      '/rebuttal',
      '/agent-transfer',
      '/turboscript',
      '/call-screener',
      '/inbox',
      '/process',
      '/help',
    ]
    return fullPageRoutes.includes(location.pathname.toLowerCase())
  }, [location.pathname])

  const pageTitle = useMemo(() => {
    const pageTitles = {
      attendance: 'Champion Bonus',
      kpi: 'KPIs',
      myfiles: 'My Files',
    } as Record<string, string>

    const pathname = location.pathname.toLowerCase().replace('/', '')
    return pageTitles[pathname] || null
  }, [location.pathname])

  const pageTitleIcon = useMemo(() => {
    const pageIcons = {
      attendance: 'moneybag',
      kpi: 'chart',
      myfiles: 'files',
    } as Record<string, string>

    const pathname = location.pathname.toLowerCase().replace('/', '')
    return pageIcons[pathname] || null
  }, [location.pathname])

  const reportLink = useMemo(() => {
    const reportLinks = {
      attendance:
        'https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=m3wtB_wfOkOAf07YBxoiNkLap65B9rhLjb-QwHvPiOBUMk9LSVI1VU1HWDhBS0ZISVpGUUFZSTBVRy4u',
    } as Record<string, string>

    const pathname = location.pathname.toLowerCase().replace('/', '')
    return reportLinks[pathname] || null
  }, [location.pathname])

  const supplementalLink = useMemo(() => {
    const supplementalLinks = {
      attendance: {
        icon: <CalendarIcon />,
        link: 'https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=m3wtB_wfOkOAf07YBxoiNkLap65B9rhLjb-QwHvPiOBURjFONTBBT0tONjFVRTlUN0lXUEdXQ09FMy4u',
        text: 'Schedule Change Request Form',
      },
    } as Record<string, SupplementalLinkType>

    const pathname = location.pathname.toLowerCase().replace('/', '')
    return supplementalLinks[pathname] || null
  }, [location.pathname])

  useEffect(() => {
    if (!session && location.pathname !== '/login') {
      saveLoginMissLocation(
        location.pathname + (location.search.length ? location.search : ''),
      )
    }
  }, [location.pathname, location.search, session])

  if (!isLoaded) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    )
  }

  if (!session) {
    return (
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <Spin size="large" />
          </div>
        }
      >
        <Routes>
          <Route path="/onelogin" element={<OneLoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="*"
            element={
              <Navigate to="/login" state={{ from: location }} replace />
            }
          />
        </Routes>
      </Suspense>
    )
  }

  if (fullPage) {
    return (
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <Spin size="large" />
          </div>
        }
      >
        <Routes>
          <Route path="/agent-transfer" element={<AgentTransferPage />} />
          <Route
            path="/call-screener"
            element={<ScriptPage roleOverwrite={'Screener'} />}
          />
          <Route
            path="/turboscript"
            element={
              <ScriptPage roleOverwrite={'newhire'} languageOverwrite={true} />
            }
          />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/process" element={<FileDisplayPage />} />
          <Route path="/help" element={<FileDisplayPage fileset="faqs" />} />
          <Route path="*" element={<IndexPage />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <Layout className={styles.layout}>
      {showSider && <LayoutSider collapsed={collapsed} />}
      <Layout className={styles.innerLayout}>
        {showHeader && (
          <LayoutHeader
            collapsed={collapsed}
            onClickCollapse={() => setCollapsed(!collapsed)}
            showMenuToggle={showSider}
            pageTitle={pageTitle}
            pageTitleIcon={pageTitleIcon}
            reportLink={reportLink}
            supplementalLink={supplementalLink}
          >
            {hasPermission(
              permissions,
              'agents:ui:action:view:impersonation',
            ) && (
              <Impersonation
                updateImpersonation={impersonateUser}
                impersonatedUser={impersonatedUserEmail}
              />
            )}
          </LayoutHeader>
        )}
        <LayoutContent>
          <Suspense
            fallback={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Spin size="large" />
              </div>
            }
          >
            <Routes>
              <Route path="/kpi" element={<KPIPage />} />
              <Route path="/myfiles" element={<MyFilesPage />} />
              <Route path="/attendance" element={<AttendancePage />} />
              <Route path="/scheduling" element={<SchedulingPage />} />
              <Route
                path="/sms"
                element={
                  <ProtectedRoute permission="agents:ui:path:read:/sms">
                    <SmsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute permission="agents:ui:path:read:/admin/users">
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/create"
                element={
                  <ProtectedRoute permission="agents:ui:path:read:/admin/users">
                    <CreateUserPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute permission="agents:ui:path:read:/admin/roles">
                    <RolesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/roles/create"
                element={
                  <ProtectedRoute permission="agents:ui:path:read:/admin/roles">
                    <CreateRolePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/permissions"
                element={
                  <ProtectedRoute permission="agents:ui:path:read:/admin/permissions">
                    <PermissionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/permissions/create"
                element={
                  <ProtectedRoute permission="agents:ui:path:read:/admin/permissions">
                    <CreatePermissionPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/logout" element={<LogoutPage />} />
              <Route path="/" element={<IndexPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </LayoutContent>
      </Layout>
    </Layout>
  )
}

export default App
